import { Link, useRoute } from "wouter";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";

const categorySlugs = new Set(["new", "disposable", "pod-system", "open-system", "liquid"]);

function isRealProductSource(sourceUrl: string) {
  try {
    const url = new URL(sourceUrl);
    const segments = url.pathname.replace(/\/$/, "").split("/").filter(Boolean);
    return url.hostname.endsWith("igetvape.com") && segments[0] === "product" && segments.length === 2 && !categorySlugs.has(segments[1].toLowerCase());
  } catch {
    return false;
  }
}

function productSlug(sourceUrl: string) {
  const base = new URL(sourceUrl).pathname.split("/").filter(Boolean).pop()?.replace(/\.html?$/i, "") || "product";
  return base.startsWith("iget-") ? `iget-${base}` : base;
}

function plainTextFromHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function PublishedProductCategory() {
  const [, params] = useRoute("/categories/:slug");
  const { data: category, isLoading } = trpc.products.getPublishedCategory.useQuery(
    { slug: params?.slug ?? "" },
    { enabled: Boolean(params?.slug) },
  );

  if (isLoading) return <main className="container py-16"><p className="text-slate-500">Loading category page…</p></main>;
  if (!category) return <main className="container py-16"><Link href="/"><Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Back to Home</Button></Link><h1 className="mt-8 text-3xl font-bold">Category page not found</h1></main>;

  return <main className="container py-10">
    <Link href="/"><Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Back to Home</Button></Link>
    <header className="mt-8 rounded-2xl bg-slate-50 p-8">
      <Badge className="bg-blue-100 text-blue-800">IGET CATEGORY</Badge>
      <h1 className="mt-3 text-4xl font-bold text-slate-950">{category.name}</h1>
      <p className="mt-3 text-slate-600">This product category page was created from the corresponding official IGET category page.</p>
    </header>
    <section className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {category.products.filter((product) => isRealProductSource(product.sourceUrl)).map((product) => <Card key={product.sourceUrl} className="overflow-hidden border-slate-200">
        <div className="flex h-56 items-center justify-center bg-slate-50 p-5">{product.thumbnailUrl ? <img src={product.thumbnailUrl} alt={product.name} className="h-full w-full object-contain" /> : <span className="text-sm text-slate-400">No thumbnail available</span>}</div>
        <div className="p-5"><Badge variant="secondary">IGET</Badge><h2 className="mt-3 text-xl font-semibold text-slate-950">{product.name}</h2>{product.summary && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{product.summary}</p>}<Link href={`/product-pages/${productSlug(product.sourceUrl)}`}><Button className="mt-5 w-full">View product page <ArrowRight className="ml-2 h-4 w-4" /></Button></Link></div>
      </Card>)}
    </section>
    {category.contentHtml && <details className="mt-10 rounded-xl border border-slate-200 p-5"><summary className="cursor-pointer font-semibold">View copied category page content</summary><div className="copied-official-content mt-5 whitespace-pre-line text-sm leading-7 text-slate-700">{plainTextFromHtml(category.contentHtml)}</div></details>}
  </main>;
}
